import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useState,
} from 'react';

import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';

type ConfirmValue = {
    title: ReactNode;
    body: ReactNode;
    resolve: (value: boolean | PromiseLike<boolean>) => void;
    customRender?: (props: ConfirmModalProps) => ReactNode;
};

type ConfirmModalProps = {
    value: ConfirmValue;
    onCancel: () => void;
    onSubmit: () => void;
};

const ConfirmModal = ({ value, onCancel, onSubmit }: ConfirmModalProps) => (
    <ConfirmationDialog
        onConfirm={onSubmit}
        onCancel={onCancel}
        title={value.title}
    >
        {value.body}
    </ConfirmationDialog>
);

type ConfirmFunction = (
    title: ConfirmValue['title'],
    body: ConfirmValue['body'],
    customRender?: (props: ConfirmModalProps) => ReactNode
) => PromiseLike<boolean>;

export const ConfirmContext = createContext<ConfirmFunction>(() =>
    Promise.resolve(true)
);

type ConfirmDialogProviderProps = {
    children: ReactNode;
};

export const ConfirmDialogProvider = ({
    children,
}: ConfirmDialogProviderProps) => {
    const [value, setValue] = useState<ConfirmValue>();

    const confirm = useCallback<ConfirmFunction>(
        async (title, body, customRender) =>
            new Promise((resolve) =>
                setValue({ title, body, resolve, customRender })
            ),
        []
    );

    const onCancel = useCallback(() => {
        value?.resolve(false);
        setValue(undefined);
    }, [value?.resolve]);

    const onSubmit = useCallback(() => {
        value?.resolve(true);
        setValue(undefined);
    }, [value?.resolve]);

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}

            {value &&
                (value.customRender ? (
                    value.customRender({ value, onCancel, onSubmit })
                ) : (
                    <ConfirmModal
                        value={value}
                        onCancel={onCancel}
                        onSubmit={onSubmit}
                    />
                ))}
        </ConfirmContext.Provider>
    );
};

export const useConfirmationDialog = () => useContext(ConfirmContext);
